import React, {
  useState,
  useRef,
  ReactNode,
  useEffect,
  useContext,
} from "react";
import { JemGenContext } from "./jemGenContext";
import {
  Ard,
  CodeId,
  DescribeChartOfAccountsCommand,
  FuseAccountingCentralDatastoreClient,
  GetArdCommand,
  GetArdResponse,
  UseCaseInclusion,
} from "@amzn/fuse-accounting-central-datastore-client";
import { InitDomain } from "../domain/domainContext";
import { ArdRegionMap, ModalMode } from "./types";
import { Domain } from "../../config/BartLambdaConfig";
import { Auth } from "aws-amplify";
import { getFacdConfig } from "src/config/fuse-accounting-central-datastore-config";
import { isProd } from "src/util";

interface JemGenContextProviderProps {
  children: ReactNode;
}

export const JemGenContextProvider: React.FC<JemGenContextProviderProps> = ({
  children,
}) => {
  const [selectedArd, setSelectedArd] = useState<Ard | null>(null);
  const [selectedArdDetailed, setSelectedArdDetailed] =
    useState<GetArdResponse | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(ModalMode.CREATE);
  const [existingArds, setExistingArds] = useState<Ard[]>([]);
  const [toolsContentSelection, setToolsContentSelection] = useState<
    string | null
  >(null);
  const [coaDescriptions, setCoaDescriptions] = useState<Map<string, string>>(
    new Map(),
  );
  const [isLoadingCoaDescriptions, setIsLoadingCoaDescriptions] =
    useState(false);
  const ardRegionMapRef = useRef<ArdRegionMap>(new Map());
  const facdClientMapRef = useRef(
    new Map<string, FuseAccountingCentralDatastoreClient>(),
  );

  const domainData = useContext(InitDomain);

  const getFacdClient = async (
    domain: Domain,
  ): Promise<FuseAccountingCentralDatastoreClient | null> => {
    const existingClient = facdClientMapRef.current.get(domain);
    if (existingClient) {
      return existingClient;
    }

    // Create new client if it doesn't exist
    try {
      const credentials = await Auth.currentCredentials();
      const essentialCredentials = Auth.essentialCredentials(credentials);
      const newClient = new FuseAccountingCentralDatastoreClient({
        ...getFacdConfig(domain),
        credentials: essentialCredentials,
      });

      facdClientMapRef.current.set(domain, newClient);

      // Set timeout to delete the client after 30 minutes (1800000 ms)
      setTimeout(
        () => {
          facdClientMapRef.current.delete(domain);
        },
        30 * 60 * 1000,
      );

      return newClient;
    } catch (error) {
      console.error(`Error creating FACD client for domain ${domain}:`, error);
      return null;
    }
  };

  const setArdRegionMap = (map: ArdRegionMap) => {
    ardRegionMapRef.current = map;
  };

  // Fetch ARD data function moved from ard-edit-modal
  const fetchArdData = async (ardId: string) => {
    setSelectedArdDetailed(null);
    try {
      const ardRegion = ardRegionMapRef.current.get(ardId);
      if (ardRegion === undefined) {
        throw Error(`Unknown region for ARD ${ardId}`);
      }
      const facdClient = await getFacdClient(ardRegion);

      if (facdClient) {
        const getArdCommand = new GetArdCommand({
          ardId: ardId,
          useCaseInclusion: UseCaseInclusion.ALL,
          withHistory: true,
        });

        const response = await facdClient.send(getArdCommand);

        if (response && response.ard) {
          setSelectedArdDetailed(response);
        }
      } else {
        console.error("No FACD client found for region:", ardRegion);
      }
    } catch (error) {
      console.error("Error fetching ARD data for selectedArdDetailed:", error);
    }
  };

  // Automatically fetch detailed ARD data when selectedArd changes
  useEffect(() => {
    if (selectedArd?.id) {
      console.log(
        "SelectedArd changed, fetching detailed data:",
        selectedArd.id,
      );
      setSelectedArdDetailed(null);
      fetchArdData(selectedArd.id);
    } else {
      console.log("No selectedArd ID, clearing selectedArdDetailed");
      setSelectedArdDetailed(null);
    }
  }, [selectedArd?.id, domainData.domain, existingArds]);

  // Fetch COA descriptions for all 7 segments when existingArds changes
  useEffect(() => {
    const fetchCoaDescriptions = async () => {
      if (existingArds.length === 0) {
        setCoaDescriptions(new Map());
        return;
      }

      // Collect all unique 7-segment values from all ARDs
      const codeIdsMap = new Map<string, CodeId>();

      // Derive segment field names from CodeId.Visitor type (excludes the _ catch-all)
      type SegmentField = Exclude<keyof CodeId.Visitor<void>, "_">;
      const segmentFields: SegmentField[] = [
        "companyCode",
        "location",
        "costCenter",
        "accountNumber",
        "productLine",
        "channel",
        "project",
      ];

      existingArds.forEach((ard) => {
        ard.usecases?.forEach((useCase) => {
          useCase.uslLines?.forEach((uslLine) => {
            // Collect each segment type
            segmentFields.forEach((field) => {
              const value = uslLine[field];
              if (value) {
                const key = `${field}:${value}`;
                if (!codeIdsMap.has(key)) {
                  codeIdsMap.set(key, { [field]: value } as unknown as CodeId);
                }
              }
            });
          });
        });
      });

      if (codeIdsMap.size === 0) {
        setCoaDescriptions(new Map());
        return;
      }

      setIsLoadingCoaDescriptions(true);

      try {
        // Use prod-FE or devo-FE region (same as validateArd)
        const region = isProd() ? "prod-FE" : "devo-FE";
        const facdClient = await getFacdClient(region);

        if (!facdClient) {
          console.error(`Failed to get FACD client for region '${region}'`);
          setCoaDescriptions(new Map());
          return;
        }

        // Build the codeIds array for the API call
        const codeIds = Array.from(codeIdsMap.values());

        const command = new DescribeChartOfAccountsCommand({ codeIds });
        const response = await facdClient.send(command);

        // Build the mapping from segment key to codeName
        // response.descriptions is already keyed by "{segment}:{codeId}" format
        const descriptionsMap = new Map<string, string>();
        if (response.descriptions) {
          Object.entries(response.descriptions).forEach(
            ([key, description]) => {
              if (description?.codeName) {
                descriptionsMap.set(key, description.codeName);
              }
            },
          );
        }

        setCoaDescriptions(descriptionsMap);
      } catch (error) {
        console.error("Failed to fetch COA descriptions:", error);
        setCoaDescriptions(new Map());
      } finally {
        setIsLoadingCoaDescriptions(false);
      }
    };

    fetchCoaDescriptions();
  }, [existingArds]);

  return (
    <JemGenContext.Provider
      value={{
        existingArds,
        setExistingArds,
        selectedArd,
        setSelectedArd,
        selectedArdDetailed,
        setSelectedArdDetailed,
        getFacdClient,
        ardRegionMap: ardRegionMapRef.current,
        setArdRegionMap,
        modalMode,
        setModalMode,
        toolsContentSelection,
        setToolsContentSelection,
        coaDescriptions,
        isLoadingCoaDescriptions,
      }}
    >
      {children}
    </JemGenContext.Provider>
  );
};
